<template>
    <SmithsChartCard :has-data="hasData" :loading="loading" title="L1 Decision Breakdown">
        <template #sidebar>
            <div class="kpi-card kpi-card--primary">
                <div class="kpi-card__value">{{ formatNumber(data?.kpis?.totalScreened ?? 0) }}</div>
                <div class="kpi-card__label">Bags Screened</div>
            </div>
            <div class="kpi-card kpi-card--success">
                <div class="kpi-card__value">{{ formatNumber(data?.kpis?.totalL1Cleared ?? 0) }}</div>
                <div class="kpi-card__label">Level 1 Cleared</div>
            </div>
            <div class="kpi-card kpi-card--warning">
                <div class="kpi-card__value">{{ formatNumber(data?.kpis?.totalOperatorClear ?? 0) }}</div>
                <div class="kpi-card__label">Operator Clear</div>
            </div>
        </template>
        <c-chart :key="componentKey" :custom-tooltips="false" :data="chartDataset" :options="chartOptions" class="h-full" type="line" />
    </SmithsChartCard>
</template>

<script setup>
import { computed, ref } from 'vue';
import { CChart } from '@coreui/vue-chartjs';
import SmithsChartCard from './SmithsChartCard.vue';
import { getBaseChartOptions, chartColors, lightTooltip } from '@/Utils/chartDefaults';

const props = defineProps({
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
});

const componentKey = ref(0);

const hasData = computed(() => props.data?.labels?.length > 0);

function formatNumber(value) {
    return new Intl.NumberFormat().format(value);
}

const chartDataset = computed(() => {
    if (!hasData.value) return { labels: [], datasets: [] };

    return {
        labels: props.data.labels,
        datasets: [
            {
                label: 'L1 Clear',
                data: props.data.l1Clear,
                backgroundColor: chartColors.primaryFill,
                borderColor: chartColors.primary,
                borderWidth: 1.5,
                fill: true,
                tension: 0.3,
                pointRadius: 0,
                order: 3,
            },
            {
                label: 'L1 Reject',
                data: props.data.l1Reject,
                backgroundColor: chartColors.redFill,
                borderColor: chartColors.red,
                borderWidth: 1.5,
                fill: true,
                tension: 0.3,
                pointRadius: 0,
                order: 2,
            },
            {
                label: 'BNA',
                data: props.data.bna,
                backgroundColor: chartColors.greenFill,
                borderColor: chartColors.green,
                borderWidth: 1.5,
                fill: true,
                tension: 0.3,
                pointRadius: 0,
                order: 1,
            },
        ],
    };
});

const chartOptions = computed(() => ({
    ...getBaseChartOptions(),
    interaction: { mode: 'index', intersect: false },
    scales: {
        x: {
            grid: { display: false },
            ticks: { font: { size: 11 } },
        },
        y: {
            stacked: true,
            beginAtZero: true,
            grid: { drawBorder: false },
        },
    },
    plugins: {
        ...getBaseChartOptions().plugins,
        tooltip: { ...lightTooltip, enabled: true, mode: 'index', intersect: false },
    },
}));
</script>

<style scoped>
.kpi-card {
    padding: 0.5rem 0.75rem;
    border-radius: 0.375rem;
    color: #fff;
}

.kpi-card--primary {
    background: #2c54ea;
}

.kpi-card--success {
    background: #27ae60;
}

.kpi-card--warning {
    background: #e67e22;
}

.kpi-card__value {
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.2;
}

.kpi-card__label {
    font-size: 0.7rem;
    text-transform: uppercase;
    opacity: 0.9;
}
</style>
